import { useEffect } from 'react';
import type { Weather, WeatherCondition, PollenLevel } from '@daily/shared';
import { weatherTheme } from '@/lib/weatherTheme';
import { format } from 'date-fns';
import {
  X,
  Sun,
  CloudSun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudFog,
  Zap,
  Flower2,
} from 'lucide-react';

interface WeatherDetailProps {
  weather: Weather;
  onClose: () => void;
}

function Icon({ condition, size = 18 }: { condition: WeatherCondition; size?: number }) {
  const props = { size, strokeWidth: 2 };
  switch (condition) {
    case 'clear':
      return <Sun {...props} />;
    case 'fair':
      return <CloudSun {...props} />;
    case 'rain':
    case 'showers':
      return <CloudRain {...props} />;
    case 'snow':
      return <CloudSnow {...props} />;
    case 'fog':
      return <CloudFog {...props} />;
    case 'thunder':
      return <Zap {...props} />;
    default:
      return <Cloud {...props} />;
  }
}

const POLLEN_BADGE: Record<PollenLevel, { label: string; bg: string; ink: string }> = {
  low: { label: 'Low', bg: '#6B8C5E', ink: '#FFFFFF' },
  moderate: { label: 'Moderate', bg: '#E8A33A', ink: '#1A1410' },
  high: { label: 'High', bg: '#E94D2C', ink: '#FFFFFF' },
  very_high: { label: 'Very high', bg: '#B83518', ink: '#FFFFFF' },
};

function pollenCount(v: number | null): string {
  if (v === null) return '—';
  return `${Math.round(v)}`;
}

export function WeatherDetail({ weather, onClose }: WeatherDetailProps) {
  const theme = weatherTheme(weather.condition);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pollen = weather.pollen;
  const badge = pollen?.level ? POLLEN_BADGE[pollen.level] : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-lg shadow-lg overflow-hidden max-h-[90vh] overflow-y-auto bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero: current conditions */}
        <div className="p-6 relative" style={{ background: theme.gradient, color: theme.ink }}>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-black/10 hover:bg-black/20"
            aria-label="Close"
          >
            <X size={16} />
          </button>
          <div className="text-[10px] tracking-[0.18em] uppercase opacity-70">Weather · Islington</div>
          <div className="flex items-end gap-3 mt-1">
            <div className="font-serif text-[56px] leading-none">
              {weather.temperatureC}
              <span className="text-[28px] align-top">°</span>
            </div>
            <div className="pb-2">
              <Icon condition={weather.condition} size={28} />
            </div>
          </div>
          <div className="text-[14px] font-medium mt-1 capitalize">
            {weather.condition}
            {weather.sub ? ` · ${weather.sub}` : ''}
          </div>
        </div>

        <div className="p-5 flex flex-col gap-5">
          {/* Pollen — surfaced prominently for the hay-fever sufferers */}
          {pollen && (
            <div className="rounded-md border border-black/[0.06] p-4 bg-cardWarm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5 text-eyebrow uppercase tracking-[0.16em] text-inkFaded">
                  <Flower2 size={13} /> Pollen
                </div>
                {badge && (
                  <span
                    className="text-[11px] font-bold tracking-[0.08em] uppercase px-2.5 py-1 rounded-full"
                    style={{ background: badge.bg, color: badge.ink }}
                  >
                    {badge.label}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {(
                  [
                    ['Grass', pollen.grass],
                    ['Tree', pollen.tree],
                    ['Weed', pollen.weed],
                  ] as const
                ).map(([label, v]) => (
                  <div key={label} className="rounded-sm bg-white py-2">
                    <div className="font-serif text-[22px] leading-none text-ink">
                      {pollenCount(v)}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.12em] text-inkFaded mt-1">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-inkFaded mt-2 leading-snug">
                grains/m³ · {badge?.label === 'Low' ? 'a good day for Jomi' : 'antihistamine weather for Jomi'}
              </div>
            </div>
          )}

          {/* Hourly strip */}
          {weather.hourly.length > 0 && (
            <div>
              <div className="text-eyebrow uppercase tracking-[0.16em] text-inkFaded mb-2">
                Next hours
              </div>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {weather.hourly.map((h) => (
                  <div key={h.time} className="flex flex-col items-center gap-1 flex-shrink-0 w-12">
                    <span className="font-mono text-[10px] text-inkFaded">
                      {format(new Date(h.time), 'HH')}
                    </span>
                    <span className="text-inkSoft">
                      <Icon condition={h.condition} size={16} />
                    </span>
                    <span className="font-serif text-[14px]">{h.temperatureC}°</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 7-day */}
          {weather.daily && weather.daily.length > 0 && (
            <div>
              <div className="text-eyebrow uppercase tracking-[0.16em] text-inkFaded mb-2">
                Next 7 days
              </div>
              <div className="flex flex-col">
                {weather.daily.map((d, i) => (
                  <div
                    key={d.date}
                    className="grid grid-cols-[1fr_auto_auto] gap-3 items-center py-2 border-b border-black/[0.04] last:border-0"
                  >
                    <span className="text-[13px] text-ink">
                      {i === 0 ? 'Today' : format(new Date(d.date), 'EEEE')}
                    </span>
                    <span className="text-inkSoft">
                      <Icon condition={d.condition} size={16} />
                    </span>
                    <span className="font-mono text-[12px] tabular-nums">
                      <span className="text-ink">{d.maxC}°</span>
                      <span className="text-inkFaded"> {d.minC}°</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
